import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Eye, EyeOff, Activity, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import OnboardingNew from './OnboardingNew';
import styles from './Auth.module.css';

interface AuthProps {
  onAuthSuccess: () => void;
}

const Auth: React.FC<AuthProps> = ({ onAuthSuccess }) => {
  // Sempre inicia mostrando onboarding para usuários não autenticados
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [authMode, setAuthMode] = useState<'main' | 'email-login' | 'email-register'>('main');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, loginWithGoogle, register } = useAuth();

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (authMode === 'email-login') {
        await login(email, password);
      } else if (authMode === 'email-register') {
        if (!name.trim()) {
          setError('Nome é obrigatório');
          return;
        }
        await register(email, password, name);
      }
      onAuthSuccess();
    } catch (error: any) {
      setError(getErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      await loginWithGoogle();
      onAuthSuccess();
    } catch (error: any) {
      console.error('Erro no login Google:', error);
      setError(getErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setName('');
    setError('');
    setShowPassword(false);
  };

  const goBack = () => {
    setAuthMode('main');
    resetForm();
  };

  const getErrorMessage = (errorCode: string) => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'Usuário não encontrado';
      case 'auth/wrong-password':
        return 'Senha incorreta';
      case 'auth/email-already-in-use':
        return 'Este email já está em uso';
      case 'auth/weak-password':
        return 'A senha deve ter pelo menos 6 caracteres';
      case 'auth/invalid-email':
        return 'Email inválido';
      case 'auth/popup-closed-by-user':
        return 'Login cancelado pelo usuário';
      case 'auth/popup-blocked':
        return 'Popup bloqueado. Permita popups para este site';
      default:
        return 'Ocorreu um erro. Tente novamente.';
    }  };

  // Se deve mostrar onboarding, renderizar o novo componente PRIMEIRO
  if (showOnboarding) {
    return (
      <OnboardingNew 
        onComplete={() => setShowOnboarding(false)}
      />
    );
  }

  // Tela principal com opções
  if (authMode === 'main') {
    return (
      <div className={styles.container}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={styles.wrapper}
        >
          {/* Logo e Header */}
          <div className={styles.header}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className={styles.logoContainer}
            >
              <Activity className={styles.logoIcon} />            </motion.div>
            <h1 className={styles.title}>Axioma Hábitos</h1>
            <p className={styles.subtitle}>Transforme sua vida através de pequenos atos diários</p>
          </div>

          {/* Container principal */}
          <motion.div
            initial={{ opacity: 0, x: 0 }}
            animate={{ opacity: 1, x: 0 }}
            className={styles.card}
          >
            <h2 className={styles.cardTitle}>
              Entre ou crie sua conta
            </h2>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={styles.errorMessage}
              >
                {error}
              </motion.div>
            )}

            <div className={styles.buttonGroup}>              {/* Botão Google */}
              <motion.button
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={styles.googleButton}
              >
                <svg className={styles.googleIcon} viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {loading ? 'Aguarde...' : 'Continuar com Google'}
              </motion.button>

              <div className={styles.divider}>
                <div className={styles.dividerLine}></div>
                <div className={styles.dividerText}>ou</div>
                <div className={styles.dividerLine}></div>
              </div>

              {/* Botão Login com Email */}
              <motion.button
                type="button"
                onClick={() => setAuthMode('email-login')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={styles.emailButton}
              >
                <Mail className={styles.buttonIcon} />
                Entrar com Email
              </motion.button>

              {/* Botão Criar Conta */}
              <motion.button
                type="button"
                onClick={() => setAuthMode('email-register')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={styles.registerButton}
              >
                <User className={styles.buttonIcon} />
                Criar Nova Conta
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    );  }  

  // Formulário de email (login ou registro)
  return (
    <div className={styles.container}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={styles.wrapper}
      >
        {/* Logo e Header */}
        <div className={styles.header}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            className={styles.logoContainer}
          >
            <Activity className={styles.logoIcon} />          </motion.div>
          <h1 className={styles.title}>Axioma Hábitos</h1>
          <p className={styles.subtitle}>
            {authMode === 'email-login' ? 'Entre com sua conta' : 'Crie sua nova conta'}
          </p>
        </div>

        {/* Formulário */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className={styles.card}
        >
          {/* Botão Voltar */}
          <button
            onClick={goBack}
            className={styles.backButton}
          >
            <ArrowLeft className={styles.backIcon} />
            Voltar
          </button>

          <h2 className={styles.cardTitle}>
            {authMode === 'email-login' ? 'Entrar' : 'Criar conta'}
          </h2>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={styles.errorMessage}
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleEmailSubmit} className={styles.form}>            {/* Campo Nome (apenas no registro) */}
            {authMode === 'email-register' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className={styles.formGroup}
              >
                <label className={styles.label}>
                  Nome completo
                </label>
                <div className={styles.inputContainer}>
                  <User className={styles.inputIcon} />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={styles.input}
                    placeholder="Seu nome completo"
                    required
                  />
                </div>
              </motion.div>
            )}

            {/* Campo Email */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Email
              </label>
              <div className={styles.inputContainer}>
                <Mail className={styles.inputIcon} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={styles.input}
                  placeholder="seu@email.com"
                  required
                />
              </div>
            </div>

            {/* Campo Senha */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Senha
              </label>
              <div className={styles.inputContainer}>
                <Lock className={styles.inputIcon} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${styles.input} ${styles.inputWithToggle}`}
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.passwordToggle}
                >
                  {showPassword ? <EyeOff className={styles.toggleIcon} /> : <Eye className={styles.toggleIcon} />}
                </button>
              </div>
              {authMode === 'email-register' && (
                <p className={styles.hint}>Mínimo de 6 caracteres</p>
              )}
            </div>            {/* Botão Submit */}
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={styles.submitButton}
            >
              {loading ? (
                <div className={styles.loadingContainer}>
                  <div className={styles.spinner}></div>
                  {authMode === 'email-login' ? 'Entrando...' : 'Criando conta...'}
                </div>
              ) : (
                authMode === 'email-login' ? 'Entrar' : 'Criar conta'
              )}
            </motion.button>
          </form>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Auth;
